import { useState } from "react";
import { Plus, Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

/**
 * Dynamic row editor for a newline-joined scope string.
 * Matches the pattern used in pages/Estimations.jsx (ScopeRowList).
 */
function ScopeRowList({ value, onChange, placeholder, addLabel, testIdBase }) {
  const rows = (value || "").split("\n");
  const emit = (next) => onChange(next.join("\n"));
  const updateRow = (idx, text) => {
    const next = rows.slice();
    next[idx] = text;
    emit(next);
  };
  const removeRow = (idx) => {
    const next = rows.slice();
    next.splice(idx, 1);
    emit(next.length ? next : [""]);
  };
  const addRow = () => emit([...rows, ""]);
  // Enter = add row. Shift+Enter is free for linebreaks inside a row.
  const onKeyDown = (idx) => (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const next = rows.slice();
      next.splice(idx + 1, 0, "");
      emit(next);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${testIdBase}-row-${idx + 1}"]`);
        if (el) el.focus();
      }, 20);
    }
  };
  // Auto-grow textarea height to fit wrapped content.
  const autoSize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  return (
    <div className="space-y-1">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-start gap-1">
          <textarea
            data-testid={`${testIdBase}-row-${idx}`}
            value={row}
            placeholder={placeholder}
            rows={1}
            ref={autoSize}
            onInput={(e) => autoSize(e.currentTarget)}
            onChange={(e) => updateRow(idx, e.target.value)}
            onKeyDown={onKeyDown(idx)}
            className="flex-1 min-h-[32px] py-1.5 px-3 rounded-md bg-white border border-gray-300 text-sm resize-none overflow-hidden leading-5 whitespace-pre-wrap break-words focus:outline-none focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20"
          />
          <button
            type="button"
            onClick={() => removeRow(idx)}
            data-testid={`${testIdBase}-remove-${idx}`}
            title="Remove"
            className="text-gray-400 hover:text-red-600 shrink-0 w-6 h-8 flex items-center justify-center rounded transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        data-testid={`${testIdBase}-add-btn`}
        className="text-xs text-md3-primary hover:underline flex items-center gap-1 mt-1"
      >
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

const genId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `li-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Lets estimators manage line items from the Estimation Dashboard
 * WITHOUT touching hours/cost/schedule fields.
 *
 * - Editable fields: description, scope_inclusions, scope_exclusions.
 * - Hours, cost, duration, and award fields are preserved untouched on save.
 * - New items start with hours/cost/schedule = 0 ("Pricing pending" chip shown).
 */
export default function LineItemScopeEditor({ lineItems, onChange }) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggleCollapsed = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const collapseAll = () => setCollapsed(new Set(items.map((i) => i.id)));
  const expandAll = () => setCollapsed(new Set());

  const updateItem = (id, field, value) => {
    onChange(items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const addItem = () => {
    const newItem = {
      id: genId(),
      description: "",
      detailing_cost: 0,
      engineering_cost: 0,
      estimated_detailing_hours: 0,
      subtotal: 0,
      duration_weeks: 0,
      duration_days: 0,
      award_status: "pending",
      awarded_date: null,
      pushed_to_project: false,
      scope_inclusions: "",
      scope_exclusions: "",
      _created_from_dashboard: true,
    };
    onChange([...items, newItem]);
  };

  const removeItem = (id) => {
    onChange(items.filter((it) => it.id !== id));
  };

  const isPricingPending = (it) =>
    (Number(it.estimated_detailing_hours) || 0) === 0
    && (Number(it.detailing_cost) || 0) === 0
    && (Number(it.engineering_cost) || 0) === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">
          Line Items — Scope Detail
          <span className="ml-2 text-xs font-normal text-gray-500">
            ({items.length} {items.length === 1 ? "item" : "items"})
          </span>
        </Label>
        {items.length > 1 && (
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={collapseAll}
              data-testid="scope-editor-collapse-all"
              className="text-md3-primary hover:underline"
            >
              Collapse All
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={expandAll}
              data-testid="scope-editor-expand-all"
              className="text-md3-primary hover:underline"
            >
              Expand All
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {items.map((item, index) => {
          const isOpen = !collapsed.has(item.id);
          const inclCount = (item.scope_inclusions || "")
            .split("\n")
            .filter((s) => s.trim()).length;
          const exclCount = (item.scope_exclusions || "")
            .split("\n")
            .filter((s) => s.trim()).length;
          const pending = isPricingPending(item);

          return (
            <div
              key={item.id}
              className="border border-gray-200 rounded-md p-3 bg-gray-50/50"
              data-testid={`scope-editor-item-${index}`}
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => toggleCollapsed(item.id)}
                  data-testid={`scope-editor-toggle-${index}`}
                  className="shrink-0 mt-2 text-gray-500 hover:text-gray-700"
                  title={isOpen ? "Collapse" : "Expand"}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 shrink-0 w-6">
                      #{index + 1}
                    </span>
                    <Input
                      data-testid={`scope-editor-desc-${index}`}
                      value={item.description || ""}
                      onChange={(e) =>
                        updateItem(item.id, "description", e.target.value)
                      }
                      placeholder="Item description (e.g., Structural and Miscellaneous)"
                      className="bg-white border-gray-300 text-sm h-9 flex-1"
                    />
                    {pending && (
                      <Badge
                        variant="outline"
                        className="shrink-0 bg-amber-50 border-amber-300 text-amber-700 text-[10px] px-1.5 py-0 h-5"
                        title="Hours and cost will be added later by the pricing team"
                      >
                        Pricing pending
                      </Badge>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      data-testid={`scope-editor-remove-${index}`}
                      title="Remove line item"
                      className="shrink-0 text-gray-400 hover:text-red-600 w-7 h-7 flex items-center justify-center rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {!isOpen && (
                    <div className="text-xs text-gray-500 pl-8">
                      {inclCount} inclusion{inclCount === 1 ? "" : "s"} · {exclCount}{" "}
                      exclusion{exclCount === 1 ? "" : "s"}
                    </div>
                  )}

                  {isOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-0 md:pl-0 pt-1">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Scope Inclusions</Label>
                        <ScopeRowList
                          value={item.scope_inclusions || ""}
                          onChange={(v) =>
                            updateItem(item.id, "scope_inclusions", v)
                          }
                          placeholder="e.g., Structural detailing"
                          addLabel="Add Inclusion"
                          testIdBase={`scope-editor-incl-${index}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Scope Exclusions</Label>
                        <ScopeRowList
                          value={item.scope_exclusions || ""}
                          onChange={(v) =>
                            updateItem(item.id, "scope_exclusions", v)
                          }
                          placeholder="e.g., Connection design"
                          addLabel="Add Exclusion"
                          testIdBase={`scope-editor-excl-${index}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center">
        <Button
          type="button"
          onClick={addItem}
          size="sm"
          variant="outline"
          data-testid="scope-editor-add-item"
          className="w-full border-dashed hover:bg-md3-surface-container-high"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Line Item
        </Button>
      </div>
    </div>
  );
}
